import { Autocomplete, Box, CircularProgress, TextField, Typography } from "@mui/material";
import React, { useEffect } from "react";
import { IDataSelectOption, IDataSelectProps } from "./dataselector.interface";
import { useDataSelect } from "./hooks/useDataSelect";

const SCROLL_THRESHOLD_PX = 24;

const getModalSelectContainer = (): Element | null => {
  if (typeof document === "undefined") {
    return null;
  }

  let container = document.getElementById("modal-select-container");

  if (!container) {
    container = document.createElement("div");
    container.id = "modal-select-container";
    container.style.zIndex = "99999999";
    document.body.appendChild(container);
  }

  return container;
};

export const DataSelect = (props: IDataSelectProps) => {
  const {
    label,
    value,
    source,
    placeholder,
    required = false,
    disabled = false,
    helperText,
    error = false,
    noOptionsText = "No options available.",
    onChange,
    onSelectedRecordChange,
  } = props;

  const { options, selectedOption, inputValue, setInputValue, isLoading, isFetchingNextPage, hasNextPage, errorMessage, loadMore } = useDataSelect(
    source,
    value,
  );

  useEffect(() => {
    onSelectedRecordChange?.(selectedOption?.raw ?? null);
  }, [onSelectedRecordChange, selectedOption]);

  return (
    <Autocomplete<IDataSelectOption, false, false, false>
      fullWidth
      options={options}
      value={selectedOption}
      disabled={disabled}
      loading={isLoading}
      inputValue={inputValue}
      filterOptions={(items) => items}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, currentValue) => option.uid === currentValue.uid}
      onInputChange={(_event, nextValue, reason) => {
        if (reason === "reset") {
          return;
        }

        setInputValue(nextValue);
      }}
      onChange={(_event, nextValue) => {
        onChange(nextValue?.uid ?? null, nextValue ?? null);
      }}
      ListboxProps={{
        onScroll: (event: React.UIEvent<HTMLUListElement>) => {
          const listbox = event.currentTarget;
          const reachedBottom = listbox.scrollTop + listbox.clientHeight >= listbox.scrollHeight - SCROLL_THRESHOLD_PX;

          if (reachedBottom && hasNextPage && !isFetchingNextPage) {
            loadMore();
          }
        },
        style: {
          maxHeight: 300,
          overflowY: "auto",
        },
      }}
      slotProps={{
        popper: {
          container: getModalSelectContainer,
          style: {
            zIndex: 99999999,
          },
        },
        paper: {
          style: {
            maxHeight: 300,
          },
        },
      }}
      noOptionsText={isLoading ? "Loading..." : inputValue.trim() ? "No matching results." : noOptionsText}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          error={error || Boolean(errorMessage)}
          helperText={errorMessage ?? helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading || isFetchingNextPage ? <CircularProgress size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(optionProps, option) => (
        <Box component="li" {...optionProps} key={option.uid}>
          <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {option.label}
            </Typography>
          </Box>
        </Box>
      )}
    />
  );
};
